import React, { FC } from 'react';

import {
  Container,
  ToggleContainer,
  ToggleContainerProps,
  useToggleContext,
} from '@lglp/components';
import {
  LinkType,
  Localization,
  LocalizationRegional,
} from '@lglp/prismic-client';
import { path } from 'ramda';
import { useTranslation } from 'react-i18next';
import { useParams, useRouteMatch } from 'react-router';

import { HOME } from 'translations';
import { translateRegion } from 'translations/mapping';

import { FR } from '../../../helpers/i18n';
import {
  routeToTypeRegion,
  TYPE_TO_ROUTE_REGION,
} from '../../../helpers/routes/mapping';
import { INTERNATIONAL, REGION } from '../../../types/filters/region';
import { LinkedComponent } from '../../LinkedComponent';
import styles from './RegionMenu.module.scss';

type RegionMenuToggleContainerProps = ToggleContainerProps & {
  regionList: RegionItem[];
  region: string;
};

const RegionMenuToggleContainer: FC<RegionMenuToggleContainerProps> = ({
  regionList,
  region,
}) => {
  const { t } = useTranslation();
  const { toggleOpen, opened } = useToggleContext();

  const closeMenu = () => {
    if (opened) {
      toggleOpen(false);
    }
  };

  return (
    <Container onMouseLeave={() => closeMenu()}>
      <ToggleContainer.Trigger className={styles.triggerButton}>
        {region &&
          region !== INTERNATIONAL &&
          t(translateRegion(routeToTypeRegion(region) as Localization))}
        {region &&
          region === INTERNATIONAL &&
          t(HOME.REGION_MENU_INTERNATIONAL)}
        {!region && t(HOME.REGION_MENU_TITLE)}
      </ToggleContainer.Trigger>
      <ToggleContainer.AnimatedContent className={styles.openingMenu}>
        <ToggleContainer.Content className={styles.linksContainer}>
          <RegionMenuContent regionList={regionList} />
        </ToggleContainer.Content>
      </ToggleContainer.AnimatedContent>
    </Container>
  );
};

const RegionMenu: FC<ToggleContainerProps> = ({ className, ...rest }) => {
  const { t, i18n } = useTranslation();
  const match = useRouteMatch();

  if (i18n.language !== FR) {
    return null;
  }

  const region = path(['params', 'regionSlug'], match);
  const regionList = Object.values(LocalizationRegional)
    .map((region) => ({
      key: region,
      label: t(translateRegion(region)),
    }))
    .sort((a, b) => {
      if (a.label > b.label) {
        return 1;
      }
      if (a.label < b.label) {
        return -1;
      }
      return 0;
    });

  return (
    <ToggleContainer className={[styles.regionMenu, className]} {...rest}>
      <RegionMenuToggleContainer
        region={region as string}
        regionList={regionList}
      />
    </ToggleContainer>
  );
};

export default RegionMenu;

type RegionItem = {
  key: LocalizationRegional;
  label: string;
};

type RegionMenuContentProps = {
  regionList: RegionItem[];
};

const RegionMenuContent: FC<RegionMenuContentProps> = ({ regionList }) => {
  const { t, i18n } = useTranslation();
  const { toggleOpen } = useToggleContext();

  const { regionSlug } = useParams<{ regionSlug: string }>();
  const activeRegion = routeToTypeRegion(regionSlug) || REGION.NATIONAL;

  const half = Math.ceil(regionList.length / 2);
  const columns = [
    regionList.filter((r, i) => i < half),
    regionList.filter((r, i) => i >= half),
  ];

  return (
    <Container className={styles.groups}>
      <Container className={styles.linksGroup}>
        <LinkedComponent
          className={[styles.link, styles.linkHome]}
          link={{
            linkType: LinkType.Web,
            url: `/${i18n.language}`,
          }}
          onClick={() => toggleOpen(false)}
        >
          {t(HOME.REGION_MENU_HOME)}
        </LinkedComponent>
      </Container>

      <Container className={[styles.linksGroup, styles.regionalLinks]}>
        {columns.map((column, columnIndex) => (
          <Container key={columnIndex}>
            {column.map((region) => (
              <LinkedComponent
                className={[
                  styles.link,
                  styles.regionalLink,
                  region.key === activeRegion && styles.active,
                ]}
                link={{
                  linkType: LinkType.Web,
                  url: `/${i18n.language}/${TYPE_TO_ROUTE_REGION[region.key]}`,
                }}
                key={region.key}
                onClick={() => toggleOpen(false)}
              >
                {region.label}
              </LinkedComponent>
            ))}
          </Container>
        ))}
      </Container>

      <Container className={styles.linksGroup}>
        <LinkedComponent
          className={[
            styles.link,
            styles.linkInternational,
            TYPE_TO_ROUTE_REGION[INTERNATIONAL] === activeRegion &&
              styles.active,
          ]}
          link={{
            linkType: LinkType.Web,
            url: `/${i18n.language}/${TYPE_TO_ROUTE_REGION[INTERNATIONAL]}`,
          }}
          onClick={() => toggleOpen(false)}
        >
          {t(HOME.REGION_MENU_INTERNATIONAL)}
        </LinkedComponent>
      </Container>
    </Container>
  );
};
